import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from 'support/helpers/cn/cn';
import { PropertyFilter } from 'support/types/filters';

import { DropdownPanelShell } from '../DropdownPanelShell';
import { FilterOptionRow } from '../FilterOptionRow';
import { OptionValue, parseOptionValues, serializeOptionValues } from './subOptionValues';

type FilterDropdownPanelWithSubOptionsProps = {
  filter: PropertyFilter;
  onChange: (filterKey: string, newValue: string) => void;
};

// The first tab is the "no sub-option" choice, serialized as a bare value.
const DEFAULT_TAB_KEY = 'DEFAULT';

export const FilterDropdownPanelWithSubOptions: FC<FilterDropdownPanelWithSubOptionsProps> = ({ filter, onChange }) => {
  const { t } = useTranslation();
  const options = filter.dropDownOptions;
  const subOptions = options?.subOptions;

  const validSubOptionValues = useMemo(() => subOptions?.values.map((value) => value.value) ?? [], [subOptions]);
  const selectedValues = useMemo(
    () => parseOptionValues(filter.value, validSubOptionValues),
    [filter.value, validSubOptionValues],
  );
  const selectedByValue = useMemo(
    () => new Map(selectedValues.map((selected) => [selected.value, selected])),
    [selectedValues],
  );

  if (!subOptions) {
    return null;
  }

  const tabs: Array<{ key: string; subOptionValue?: string; label: string }> = [
    { key: DEFAULT_TAB_KEY, subOptionValue: undefined, label: subOptions.defaultLabel },
    ...subOptions.values.map((value) => ({ key: value.value, subOptionValue: value.value, label: value.label })),
  ];

  const toggleValue = (value: string) => {
    const next: Array<OptionValue> = selectedByValue.has(value)
      ? selectedValues.filter((selected) => selected.value !== value)
      : [...selectedValues, { value }];
    onChange(filter.key, serializeOptionValues(next));
  };

  const setSubOptionValue = (value: string, subOptionValue?: string) => {
    const next = selectedValues.map((selected) => (selected.value === value ? { value, subOptionValue } : selected));
    onChange(filter.key, serializeOptionValues(next));
  };

  return (
    <DropdownPanelShell filter={filter} className="flex max-h-96 w-72 flex-col">
      {(filteredOptions) =>
        filteredOptions.map((option) => {
          const value = String(option.value);
          const selectedValue = selectedByValue.get(value);
          const selected = selectedValue !== undefined;
          const activeSubOptionValue = selectedValue?.subOptionValue ?? undefined;

          return (
            <FilterOptionRow
              key={value}
              type="checkbox"
              name={filter.key}
              selected={selected}
              onSelect={() => toggleValue(value)}
              testId={`filter-option-${filter.key}-${value}`}
              footer={
                selected ? (
                  <div className="-mt-0.5 pb-2 pl-11">
                    <div className="flex w-fit gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
                      {tabs.map((tab) => {
                        const isActive = activeSubOptionValue === tab.subOptionValue;
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => setSubOptionValue(value, tab.subOptionValue)}
                            data-testid={`filter-suboption-${filter.key}-${value}-${tab.key}`}
                            className={cn(
                              'rounded-sm px-2.5 py-0.5 text-xs focus-visible:outline-hidden',
                              isActive ? 'bg-gray-900 font-medium text-white' : 'text-gray-600 hover:bg-gray-100',
                            )}
                          >
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : undefined
              }
            >
              {t(option.label)}
            </FilterOptionRow>
          );
        })
      }
    </DropdownPanelShell>
  );
};
